import { Link, useNavigate } from "@tanstack/react-router";
import {
  BookOpen,
  CalendarCheck,
  ClipboardList,
  LineChart,
  RotateCcw,
  Wrench,
} from "lucide-react";
import { useAuthGate } from "@/components/auth/auth-gate";
import { NeuralButton } from "@/components/app/neural-button";
import { APP_HOME, EXAMS, rememberExam, type ExamConfig } from "@/lib/exams";
import { CheckList, FeatureCard, MarketingPage, MarketingSection } from "./marketing-kit";

const FEATURES = [
  {
    icon: BookOpen,
    title: "Syllabus & lecture tracker",
    body: "Add chapters, tick lectures as you finish them, and see real completion instead of a rough guess.",
  },
  {
    icon: RotateCcw,
    title: "Spaced revision planner",
    body: "Every finished lecture returns on a schedule, and anything you miss carries forward.",
  },
  {
    icon: ClipboardList,
    title: "Test tracker",
    body: "Plan revision before a test, then log subject-wise marks after it.",
  },
  {
    icon: LineChart,
    title: "Performance analytics",
    body: "Averages, best and worst scores, subject-wise percentages and your improvement trend.",
  },
  {
    icon: CalendarCheck,
    title: "Daily plan & streaks",
    body: "Revisions, question targets and test tasks become one honest list for today.",
  },
  {
    icon: Wrench,
    title: "Toolkit, simulations & labs",
    body: "Periodic table, formula sheets, converters, interactive simulations and virtual labs.",
  },
];

/** Reusable, data-driven landing page for every supported exam. */
export function ExamLanding({ exam }: { exam: ExamConfig }) {
  const { isAuthenticated, openAuthModal } = useAuthGate();
  const navigate = useNavigate();

  const open = () => {
    rememberExam(exam.slug);
    const go = () => navigate({ to: APP_HOME });
    if (isAuthenticated) go();
    else openAuthModal(go);
  };

  return (
    <MarketingPage>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {exam.name} preparation
      </p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">{exam.heading}</h1>
      <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">{exam.intro}</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {exam.subjects.map((s) => (
          <span
            key={s}
            className="glass rounded-full border border-hairline px-3.5 py-1.5 text-xs font-semibold"
          >
            {s}
          </span>
        ))}
      </div>

      <div className="mt-7 flex flex-wrap gap-3">
        <NeuralButton type="button" onClick={open}>
          Open The Quantum Minds
        </NeuralButton>
        <Link
          to="/features"
          className="glass inline-flex items-center justify-center rounded-full border border-hairline px-6 py-3 text-sm font-semibold transition-transform active:scale-95"
        >
          Explore features
        </Link>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Free to explore. You only sign in when you want to save and sync your progress.
      </p>

      <MarketingSection title={`Why ${exam.shortName} aspirants use it`}>
        <CheckList items={exam.points} />
      </MarketingSection>

      <MarketingSection title="What you get inside">
        <div className="grid gap-4 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </MarketingSection>

      <MarketingSection title="How it works">
        <ol className="space-y-3">
          {[
            `Add your ${exam.shortName} chapters, subject by subject.`,
            "Tick lectures as you complete them — revisions get scheduled automatically.",
            "Follow today's plan: revisions due, question targets and test tasks.",
            "Log every test and watch your subject-wise trend.",
          ].map((step, i) => (
            <li key={step} className="glass flex gap-3 rounded-2xl border border-hairline p-4">
              <span className="neural-gradient grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold text-white">
                {i + 1}
              </span>
              <span className="text-sm leading-relaxed text-muted-foreground">{step}</span>
            </li>
          ))}
        </ol>
      </MarketingSection>

      <MarketingSection title="Other exams">
        <div className="flex flex-wrap gap-2.5">
          {EXAMS.filter((e) => e.slug !== exam.slug).map((e) => (
            <Link
              key={e.slug}
              to={e.route}
              className="glass inline-flex items-center rounded-full border border-hairline px-4 py-2 text-xs font-semibold transition-transform active:scale-95"
            >
              {e.shortName}
            </Link>
          ))}
        </div>
      </MarketingSection>

      <div className="glass relative mt-12 overflow-hidden rounded-[28px] border-hairline p-6 text-center">
        <div
          aria-hidden
          className="neural-gradient pointer-events-none absolute -top-16 left-1/2 size-40 -translate-x-1/2 rounded-full opacity-25 blur-3xl"
        />
        <h2 className="relative text-[20px] font-extrabold tracking-tight">
          Start tracking your {exam.shortName} preparation
        </h2>
        <p className="relative mt-2 text-sm text-muted-foreground">
          Free to start. Create an account when you want to save and sync your progress.
        </p>
        <div className="relative mt-5 flex justify-center">
          <NeuralButton type="button" onClick={open}>
            Open The Quantum Minds
          </NeuralButton>
        </div>
      </div>
    </MarketingPage>
  );
}
